import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment-timezone";

import ScheduleHeader from "../../../components/Schedule/ScheduleHeader";
import ScheduleAlerts from "../../../components/Schedule/ScheduleAlerts";
import Calendar from "../../../components/Schedule/Calendar";
import EventDetail from "../../../components/Schedule/EventDetail";
import {
  openEventDetail,
  closeEventDetail,
  setCurrentView,
  setCurrentDate,
  clearError,
} from "../../../store/slices/scheduleSlice";
import "../../../assets/css/schedule.css";
import useScheduleData from "../../../hooks/Dashboard/Schedule/useScheduleData";
import EmbedModal from "../../../components/models/EmbedModal";
import { toast } from "react-toastify";



const Schedule = () => {
  const dispatch = useDispatch();
  const { currentView, currentDate, isDetailOpen } = useSelector(
    (state) => state.schedule
  );
  const { user } = useSelector(
    (state) => state.auth
  );
  const [hasInteracted, setHasInteracted] = useState(false);
  const [hasTriedExport, setHasTriedExport] = useState(false);
  const exportErrorTimeout = React.useRef(null);
  const [isEmbedModalOpen, setIsEmbedModalOpen] = useState(false);

  // Example: Replace handleExportEvents or add a new handler
  const handleOpenEmbedModal = () => {
    setIsEmbedModalOpen(true);
  };

  const {
    events,
    eventsLoading,
    tagsError,
    exportError,
    eventsError,
    clearEventsError,
    clearTagsError,
    clearExportError,
    refetchEvents,
    refetchExport,
  } = useScheduleData(currentDate, currentView);

  const [timezone, setTimezone] = useState(user?.attributes?.stationTimezone);

  React.useEffect(() => {
    refetchEvents(currentDate, currentView);
    setTimezone(user?.attributes?.stationTimezone)
  }, [user.attributes.stationTimezone]);

  const formatErrorMessage = (error) => {
    if (typeof error === "string") {
      return error;
    }
    if (error && typeof error === "object") {
      return error.message || error.error || JSON.stringify(error);
    }
    return "An error occurred";
  };

  const handleNavigate = (newDate) => {
    setHasInteracted(true);
    dispatch(setCurrentDate(newDate));
    refetchEvents(newDate, currentView);
  };

  const handleViewChange = (newView) => {
    setHasInteracted(true);
    dispatch(setCurrentView(newView));
    refetchEvents(currentDate, newView);
  };

  const handleSelectEvent = (event) => {
      if(event.id){
        dispatch(openEventDetail(event));
      }

  };

  const handleSelectSlot = (opt) => {
    const { start, end } =opt;
    setHasInteracted(true);
    const startMoment = moment(start);
    const endMoment = moment(end);
    const newEvent = {
      attributes: {
        title: "",
        description: "",
        startDate: startMoment.format("YYYY-MM-DD"),
        startTime: startMoment.format("HH:mm"),
        endDate: end,
        duration: endMoment.diff(startMoment, "minutes"),
        tags: [],
        hosts: [],
        allDay: startMoment.isSame(startMoment.clone().startOf("day")),
      },
    };
    dispatch(openEventDetail(newEvent));
  };

  const handleCloseModal = () => {
    dispatch(closeEventDetail());
  };

  const handleErrorClear = () => {
    dispatch(clearError());
  };

  

  React.useEffect(() => {
    if (hasTriedExport && exportError) {
      if (exportErrorTimeout.current) clearTimeout(exportErrorTimeout.current);
      exportErrorTimeout.current = setTimeout(() => {
        setHasTriedExport(false);
        clearExportError();
      }, 5000);
    }
    return () => {
      if (exportErrorTimeout.current) clearTimeout(exportErrorTimeout.current);
    };
  }, [exportError, hasTriedExport]);

  const handleCreateEvent = () => {
    dispatch(openEventDetail(null));
  };

  // Snackbar state
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const handleShowSnackbar = (msg) => {
    toast.success(msg, {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  };

  return (
      <>

      <ScheduleAlerts
        eventsError={eventsError}
        tagsError={tagsError}
        exportError={exportError}
        hasInteracted={hasInteracted}
        formatErrorMessage={formatErrorMessage}
        onClearEventsError={() => {
          clearEventsError();
          handleErrorClear();
        }}
        onClearTagsError={clearTagsError}
        onClearExportError={clearExportError}
      />
        <Calendar
          events={events}
          onSelectEvent={handleSelectEvent}
          onSelectSlot={handleSelectSlot}
          onNavigate={handleNavigate}
          onView={handleViewChange}
          currentView={currentView}
          currentDate={currentDate}
          loading={eventsLoading}
          timezone={timezone}
          bgColor={user?.attributes?.stationTheme}
          onCreate={handleCreateEvent}
          
          generateEmbedCode={handleOpenEmbedModal}
        />
      <EventDetail
        isOpen={isDetailOpen}
        onClose={handleCloseModal}
        refetchEvents={refetchEvents}
        onShowSnackbar={handleShowSnackbar}
      />
      {/* Snackbar at bottom right */}
      {showSnackbar && (
        <div
          style={{
            position: "fixed",
            bottom: 24,
            right: 24,
            zIndex: 1200,
            minWidth: 260,
            background: "#2563eb",
            color: "#fff",
            boxShadow: "0 2px 8px rgba(37,99,235,0.12)",
            borderRadius: 8,
            padding: "12px 24px",
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span>{snackbarMsg}</span>
          <button
            onClick={() => setShowSnackbar(false)}
            style={{
              background: "none",
              border: "none",
              color: "#fff",
              fontSize: 18,
              marginLeft: 12,
              cursor: "pointer",
            }}
          >
            &times;
          </button>
        </div>
      )}
      <EmbedModal
        isOpen={isEmbedModalOpen}
        toggle={() => setIsEmbedModalOpen(false)}
        owner={user} // Pass dynamic owner value
      />
    </>
  );
};

export default Schedule;
